/**
 * @leadership/roles — configurable stake permission model.
 *
 * One source of truth for "which stake roles may perform which actions". The
 * grant for each action defaults to the `canEditStake` set (stake presidency +
 * clerk + secretary); `superadmin` is always allowed and is never stored in a
 * grant list. A stake admin can override the defaults from the Role
 * permissions settings panel, which writes a `PermissionConfig` to
 * `organizations/{stakeId}/data/permissions` (under the usual `value` wrapper).
 *
 * ⚠️ apps/functions is CommonJS and cannot import this ESM module, so the same
 * tables are hand-mirrored in `apps/functions/lib/permissions.js`, and the
 * defaults are inlined again in `firestore.rules`. A drift-guard Vitest
 * (`apps/web/src/shared/lib/permissions.test.ts`) fails if the TS and JS copies
 * diverge. Keep all three in sync.
 */

import type { StakeAccountRole } from './index'

// The gated actions, as stable permission keys.
export const PERMISSION_KEYS = [
  'calling.create',
  'ordination.create',
  'meeting.agenda.add',
  'meeting.notes.edit',
  'actionItem.createForOthers',
  'actionItem.viewOthers',
  'interviews.view',
  'ward.create',
  // Missionary Work module (the public /missionary page + its leader console).
  'missionary.meals.edit',
  'missionary.helpers.view',
  'missionary.rotation.edit',
  'missionary.roster.edit',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

/**
 * Every role that can appear in a grant list. Almost all of them are stake
 * roles; `ward_mission_leader` is the one ward role that carries a stake-side
 * permission, and even then only within its own ward — see `canEditMealSlot`,
 * which is what actually enforces the ward scope.
 */
export type GrantableRole = StakeAccountRole | 'ward_mission_leader'

// Column metadata for the settings-panel matrix.
export const PERMISSION_DEFS: Record<PermissionKey, { label: string; help: string }> = {
  'calling.create': {
    label: 'Create callings',
    help: 'Start a new calling in the Callings tab.',
  },
  'ordination.create': {
    label: 'Create ordinations',
    help: 'Start a new ordination in the Ordinations tab.',
  },
  'meeting.agenda.add': {
    label: 'Add agenda items',
    help: 'Add business / agenda items to a meeting.',
  },
  'meeting.notes.edit': {
    label: 'Edit meeting notes',
    help: 'Edit the notes on a meeting.',
  },
  'actionItem.createForOthers': {
    label: 'Assign action items to others',
    help: 'Create action items assigned to other people. Everyone can always create items for themselves.',
  },
  'actionItem.viewOthers': {
    label: "View others' action items",
    help: 'See action items assigned to people other than yourself. Everyone can always see their own.',
  },
  'interviews.view': {
    label: 'View Interviews page',
    help: 'Open the Interviews page and see scheduled interviews.',
  },
  'ward.create': {
    label: 'Create wards',
    help: 'Create a new ward unit under this stake.',
  },
  'missionary.meals.edit': {
    label: 'Reassign meal host wards',
    help: 'Change which ward hosts a companionship on the missionary meal calendar. A ward mission leader can only change weeks assigned to their own ward.',
  },
  'missionary.helpers.view': {
    label: 'View visit helpers',
    help: 'See the members who have offered to go on teaching visits, with their phone numbers.',
  },
  'missionary.rotation.edit': {
    label: 'Edit the recurring schedule',
    help: 'Set the repeating host-ward rotation for each companionship. The rotation is a stake decision.',
  },
  'missionary.roster.edit': {
    label: 'Edit missionaries & groups',
    help: 'Add, edit and remove missionaries, companionship groups, and ward mission leader contacts.',
  },
}

/**
 * Roles offered as rows in the permissions matrix, in display order. Excludes
 * `viewer` (never grantable — implicitly view-only) and `superadmin` (always
 * allowed; the editor renders it as a locked-on row and never stores it).
 * `high_council` and `stake_leader` are grantable but off by default.
 */
export const PERMISSION_GRANTABLE_ROLES: GrantableRole[] = [
  'stake_president',
  'stake_counselor1',
  'stake_counselor2',
  'stake_exec_secretary',
  'stake_clerk',
  'high_council',
  'stake_leader',
  // Listed so a stake president can adjust the missionary keys for ward
  // mission leaders from the settings panel without a code change.
  'ward_mission_leader',
]

/**
 * Default grant applied to every action when the config (or a single key) is
 * absent: stake presidency + clerk + secretary — i.e. the `canEditStake` set
 * minus `superadmin` (which is always-true in code). `high_council`,
 * `stake_leader`, and viewers are excluded by default.
 */
export const DEFAULT_GRANTED_ROLES: StakeAccountRole[] = [
  'stake_president',
  'stake_counselor1',
  'stake_counselor2',
  'stake_clerk',
  'stake_exec_secretary',
]

/**
 * Keys whose default grant is wider than `DEFAULT_GRANTED_ROLES`. Everything
 * absent from this table defaults to the edit roles alone.
 *
 * The two missionary keys listed here are the day-to-day running of the meal
 * calendar, which is exactly the job a ward mission leader is called to do —
 * so they and `stake_leader` are granted by default rather than needing the
 * stake president to switch them on first. The other two missionary keys
 * (`rotation.edit`, `roster.edit`) are deliberately NOT here: the rotation and
 * the roster are stake decisions.
 */
const EXTRA_DEFAULT_GRANTS: Partial<Record<PermissionKey, GrantableRole[]>> = {
  'missionary.meals.edit': ['stake_leader', 'ward_mission_leader'],
  'missionary.helpers.view': ['stake_leader', 'ward_mission_leader'],
}

export const DEFAULT_PERMISSION_GRANTS: Record<PermissionKey, GrantableRole[]> =
  PERMISSION_KEYS.reduce(
    (acc, key) => {
      acc[key] = [...DEFAULT_GRANTED_ROLES, ...(EXTRA_DEFAULT_GRANTS[key] || [])]
      return acc
    },
    {} as Record<PermissionKey, GrantableRole[]>,
  )

export const PERMISSION_CONFIG_VERSION = 1

/**
 * Shape persisted at `organizations/{stakeId}/data/permissions` (under `value`).
 * Deliberately tolerant of partial / missing data: an absent key falls back to
 * that key's built-in default grant rather than denying outright.
 */
export interface PermissionConfig {
  version?: number
  grants?: Partial<Record<PermissionKey, string[]>>
}

/**
 * Can `role` perform `key`, given the (possibly absent) stake permission config?
 * - `superadmin` is always allowed.
 * - A missing config, or a missing entry for this key, falls back to the
 *   built-in default grant for that key.
 * - An explicit empty grant `[]` denies everyone but `superadmin`.
 */
export function hasPermission(
  key: PermissionKey,
  role: string | null | undefined,
  config?: PermissionConfig | null,
): boolean {
  if (role === 'superadmin') return true
  if (!role) return false
  const grant = config?.grants?.[key]
  const allowed = grant ?? DEFAULT_PERMISSION_GRANTS[key]
  return allowed.includes(role)
}

/**
 * Who may EDIT the permission matrix itself. Hardwired (NOT config-driven) so
 * nobody can grant themselves the ability to escalate — only the stake
 * president and superadmins. Mirrored by the `data/permissions` write rule in
 * firestore.rules.
 */
export function canEditPermissionsConfig(role: string | null | undefined): boolean {
  return role === 'stake_president' || role === 'superadmin'
}

// ─── Ward-scoped missionary permissions ────────────────────────────────────

/**
 * May this user reassign the host ward for one meal slot?
 *
 * `hasPermission` is role-only and can't express "…but only for their own
 * ward", which is precisely the ward mission leader's authority — so this
 * wrapper sits beside it and adds the scope:
 *
 *   - `superadmin` and stake roles holding `missionary.meals.edit` → any ward
 *   - `ward_mission_leader` → only when the slot's host ward is their own
 *   - everyone else → no
 *
 * A slot whose host ward is a deliberate break week (`targetWardId` null) has
 * no owning ward, so only stake-side editors can touch it — otherwise every
 * WML in the stake could claim an empty week.
 *
 * ⚠️ The public /missionary calendar is an open link, so this check is only
 * an affordance. The real boundary is the `missionary` write rules in
 * firestore.rules and the validation on the public API routes.
 */
export function canEditMealSlot(
  role: string | null | undefined,
  opts: {
    userWardId?: string | null
    targetWardId?: string | null
    config?: PermissionConfig | null
  },
): boolean {
  if (!role) return false
  if (role === 'superadmin') return true
  if (!hasPermission('missionary.meals.edit', role, opts.config)) return false
  if (role === 'ward_mission_leader') {
    return !!opts.userWardId && !!opts.targetWardId && opts.userWardId === opts.targetWardId
  }
  return true
}
